/* ==========================================================================
   MAAREE PDP add-on
   1) Find my size  - opens the MAAREE Fit Finder, then shows the recommended
      size for this style and remembers it across PDPs (reads the Fit Finder's
      own localStorage, maaree_fit_v1, and its maaree:fit-result event).
   2) Multipack     - picking a pack sets the product form quantity so a Shopify
      automatic quantity discount applies. No fake prices; display only.
      Tiers grey out when the selected variant doesn't have enough stock
      (e.g. only 2 left -> Pack of 3 greys; only 1 left -> packs of 2 and 3 grey).
   3) Image overlays (Batch 1, 11 Jun 2026) - move the eyebrow + model-size copy
      ON to the first gallery image (Boody style), and add a mobile
      "pairs well with" pill that jumps to the upsell lower down the page.
   4) Sold-out sizes (14 Jun 2026) - grey + strike through unavailable sizes,
      with a cross-colour hierarchy:
        colour    -> a colour greys if the selected size is OOS in that colour
                     (already handled by <product-grouping> in the theme)
        cup       -> greys only if (selected underband + cup) is OOS in EVERY colour
        underband -> greys only if ALL of its cups are OOS in every colour
      The cross-colour truth comes from <product-grouping>'s .rm-groupings__data.
      With no colour group we fall back to this product's own stock.
   5) Size-unit resync (22 Jul 2026) - after every rebuild pass we also re-run
      variant-picker.liquid's UK/AU (or other local) size-unit conversion via
      window.MaareeSizeUnit.refresh(). Maestrooo's variant re-render replaces
      the underband/cup swatches (and the size-unit toggle itself) on every
      colour/size change, which can land after that script's own one-shot
      160ms window -- leaving some sizes showing their raw UK number instead
      of the converted one, or a stale duplicate mid-rebuild. Tying it into
      this already-debounced, loop-safe rebuild loop keeps the two in sync on
      every pass instead of racing each other.
   Loaded after maaree-fit-finder-data.js and maaree-fit-finder.js.
   ========================================================================== */
(function () {
  'use strict';

  var MOBILE_Q = '(max-width: 699px)';
  function isMobile() { return window.matchMedia(MOBILE_Q).matches; }

  /* --------------------------- Find my size --------------------------- */
  function findSizeButtons() {
    return Array.prototype.slice.call(document.querySelectorAll('.maaree-find-size'));
  }

  function openFinder(styleKey) {
    if (window.MaareeFitFinder && typeof window.MaareeFitFinder.openDrawer === 'function') {
      window.MaareeFitFinder.openDrawer({
        currentProductKey: styleKey,
        products: window.MAAREE_FF_PRODUCTS || {},
        urls: window.MAAREE_FF_URLS || {},
        onComplete: applyResult
      });
    }
  }

  function applyResult(payload) {
    if (!payload || !payload.recommendations) return;
    findSizeButtons().forEach(function (btn) {
      var key = btn.getAttribute('data-style') || 'solidarity';
      var rec = payload.recommendations[key];
      var label = btn.querySelector('.maaree-find-size__label');
      if (!rec || rec.unavailable || !rec.size) {
        btn.removeAttribute('data-size');
        btn.classList.add('is-done');         // keep the red pill styling
        btn.classList.add('is-unavailable');  // but show the unavailable label
        if (label) label.textContent = labelUnavailable();
        return;
      }
      btn.classList.remove('is-unavailable');
      btn.setAttribute('data-size', rec.size);
      if (label) label.textContent = labelDone(rec.size);
      btn.classList.add('is-done');
      // Actual variant selection is handled by the size gate (enforceSizeGate),
      // which only selects the recommended size if it is in stock for this product.
    });
  }

  /* Short labels on mobile, full labels on desktop. */
  function labelDone(size) {
    return isMobile() ? ('Your size: ' + size) : ('Recommended size in this style: ' + size);
  }
  function labelTodo() {
    return isMobile() ? 'Fit finder' : 'Find my size';
  }
  function labelUnavailable() {
    return 'Not available in your size';
  }
  function refreshFitLabels() {
    findSizeButtons().forEach(function (btn) {
      var label = btn.querySelector('.maaree-find-size__label');
      if (!label) return;
      var next;
      if (btn.classList.contains('is-unavailable')) {
        next = labelUnavailable();
      } else if (btn.classList.contains('is-done')) {
        var size = btn.getAttribute('data-size');
        next = size ? labelDone(size) : label.textContent;
      } else {
        next = labelTodo();
      }
      // only write when it actually changes, so a stable variant produces no
      // DOM mutation and the observer above stays quiet
      if (next != null && label.textContent !== next) label.textContent = next;
    });
  }

  /* Best-effort: nudge the theme variant picker to the recommended size. */
  function trySelectVariant(rec) {
    var candidates = [];
    if (rec.size) candidates.push(String(rec.size));
    if (rec.band) candidates.push(String(rec.band));
    if (rec.cup) candidates.push(String(rec.cup));

    var inputs = document.querySelectorAll('variant-picker input[type="radio"], .product-info input[type="radio"]');
    candidates.forEach(function (val) {
      Array.prototype.forEach.call(inputs, function (input) {
        if (input.checked) return;
        var v = (input.value || '').trim();
        if (v && v.toLowerCase() === val.toLowerCase() && !input.disabled) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  function restoreStored() {
    if (!(window.MaareeFitFinder && typeof window.MaareeFitFinder.getStored === 'function')) return;
    var stored = window.MaareeFitFinder.getStored();
    if (stored) applyResult(stored);
  }

  /* ============================================================
     SOLD-OUT SIZES — cross-colour aware greying
     ============================================================ */

  function norm(s) { return ('' + s).toUpperCase().replace(/\s+/g, ''); }

  /* Always resolve stock/size-matching off the true raw UK value (data-uk,
     written server-side by swatch.liquid), never off whatever text happens to
     be visible. The UK/AU size-unit toggle now swaps the swatch's real visible
     text to show converted AU numbers -- reading textContent here would try to
     match an AU number ("12") against inventory data keyed in raw UK terms
     ("34E"), which never matches and greys out every size. Falls back to
     textContent only for older/unrelated swatches that never got a data-uk. */
  function swatchText(lbl) {
    var uk = lbl.getAttribute('data-uk');
    if (uk) return uk.trim();
    var s = lbl.querySelector('span');
    return ((s ? s.textContent : lbl.textContent) || '').trim();
  }

  function sizeKey(u, c) { return norm('' + u + c); }

  /* Recreate the size string the way <product-grouping> does: drop the colour
     option (at its index) from a variant's comma-joined options, keep the rest. */
  function sizeFromOptions(optStr, idx) {
    var parts = ('' + (optStr || '')).split(',').map(function (s) { return s.trim(); });
    if (!isNaN(idx) && idx >= 0 && idx < parts.length) parts.splice(idx, 1);
    return norm(parts.join(','));
  }

  /* Cross-colour availability from <product-grouping>'s embedded JSON.
     Returns { available: {sizeKey:true}, known: {sizeKey:true} } or null. */
  function readGroupAvailability() {
    var dataEl = document.querySelector('.rm-groupings__data');
    if (!dataEl) return null;
    var json;
    try { json = JSON.parse(dataEl.textContent); } catch (e) { return null; }

    var pg = document.querySelector('product-grouping');
    var idxAttr = pg ? pg.getAttribute('data-index') : null;
    var idx = (idxAttr && idxAttr !== 'n/a') ? parseInt(idxAttr, 10) : NaN;

    var available = {}, known = {};
    Object.keys(json).forEach(function (pid) {
      var vs = (json[pid] && json[pid].variants) || {};
      Object.keys(vs).forEach(function (vid) {
        var v = vs[vid];
        var key = sizeFromOptions(v.options, idx);
        if (!key) return;
        known[key] = true;
        if (String(v.variant_available) === 'true') available[key] = true;
      });
    });
    return Object.keys(known).length ? { available: available, known: known } : null;
  }

  /* Fallback: this product's own stock, read from the hidden real-option row the
     theme renders inside the combined selector (.is-disabled = sold out here). */
  function readLocalAvailability(root) {
    var available = {}, known = {};
    root.querySelectorAll('.variant-picker__option-values.hidden .block-swatch').forEach(function (lbl) {
      var s = swatchText(lbl);
      if (!s) return;
      var key = norm(s);
      known[key] = true;
      if (!lbl.classList.contains('is-disabled')) available[key] = true;
    });
    return { available: available, known: known };
  }

  function checkedValue(scope) {
    if (!scope) return '';
    var input = scope.querySelector('input.combined-input:checked, input[type="radio"]:checked');
    return input ? (input.value || '').trim() : '';
  }

  /* Three states for a size swatch:
       'ok'   -> selectable, no strike
       'oos'  -> out of stock in the colour being viewed, available in another
                 colour. Crossed (.mz-oos-colour). STILL selectable, so the
                 shopper can pick it and explore other sizes / colours.
       'gone' -> out of stock in every colour. Greyed + crossed (.is-disabled).
                 Still selectable (visual signal only). */
  function setState(lbl, state) {
    lbl.classList.toggle('is-disabled', state === 'gone');
    lbl.classList.toggle('mz-oos-colour', state === 'oos');
    if (state === 'gone' || state === 'oos') { lbl.setAttribute('aria-disabled', 'true'); }
    else { lbl.removeAttribute('aria-disabled'); }
  }

  /* Back-compat shim for the single-row picker path. */
  function setDisabled(lbl, disabled) { setState(lbl, disabled ? 'gone' : 'ok'); }

  function splitSelectors() {
    return Array.prototype.slice.call(document.querySelectorAll('.variant-picker__combined-selector'));
  }

  /* Split underband + cup selector.
       group -> cross-colour availability (in stock in ANY colour), or null
       local -> this product/colour's own stock
     Out of stock in THIS colour but available elsewhere -> crossed ('oos').
     Out of stock in every colour -> greyed + crossed ('gone'). Both stay
     selectable; the cross is a visual signal only. */
  function applySplitOne(root, group, local) {
    var hasGroup = group && Object.keys(group.known).length;
    var hasLocal = local && Object.keys(local.known).length;
    if (!hasGroup && !hasLocal) return;

    var fieldsets = root.querySelectorAll('fieldset');
    var ubFs = fieldsets[0];
    var cupFs = fieldsets[1];
    if (!ubFs || !cupFs) return;

    function knownAnywhere(key) { return (hasGroup && group.known[key]) || (hasLocal && local.known[key]); }
    function availAnywhere(key) { return hasGroup ? !!group.available[key] : (hasLocal ? !!local.available[key] : false); }
    function availHere(key) { return hasLocal ? !!local.available[key] : availAnywhere(key); }

    var curUb = checkedValue(ubFs);
    var cupLabels = Array.prototype.slice.call(cupFs.querySelectorAll('.variant--cup .block-swatch'));
    var cups = cupLabels.map(swatchText);

    cupLabels.forEach(function (lbl) {
      var c = swatchText(lbl);
      if (!curUb) { setState(lbl, 'ok'); return; }
      var key = sizeKey(curUb, c);
      if (!knownAnywhere(key) || !availAnywhere(key)) { setState(lbl, 'gone'); return; }
      if (!availHere(key)) { setState(lbl, 'oos'); return; }
      setState(lbl, 'ok');
    });

    var ubRow = ubFs.querySelector('.variant-picker__option-values:not(.hidden)');
    if (ubRow) {
      ubRow.querySelectorAll('.block-swatch').forEach(function (lbl) {
        var u = swatchText(lbl);
        var anyAnywhere = cups.some(function (c) { return availAnywhere(sizeKey(u, c)); });
        if (!anyAnywhere) { setState(lbl, 'gone'); return; }
        var anyHere = cups.some(function (c) { return availHere(sizeKey(u, c)); });
        setState(lbl, anyHere ? 'ok' : 'oos');
      });
    }
  }

  /* Simple single-row size pickers (Empower, Uplift, shorts...). Only override the
     theme's per-colour greying when we actually have cross-colour data for the size. */
  function applySimple(data) {
    if (!data) return;
    document.querySelectorAll('variant-picker .variant-picker__option-values:not(.hidden)').forEach(function (row) {
      if (row.closest('.variant-picker__combined-selector')) return; // split handled above
      row.querySelectorAll('.block-swatch, .thumbnail-swatch').forEach(function (lbl) {
        var s = swatchText(lbl);
        if (!s) return;
        var key = norm(s);
        if (!data.known[key]) return; // unknown size token -> leave theme default
        setDisabled(lbl, !data.available[key]);
      });
    });
  }

  function applySoldOut() {
    var group = readGroupAvailability();          // cross-colour, or null
    splitSelectors().forEach(function (root) {
      applySplitOne(root, group, readLocalAvailability(root));
    });
    if (group) applySimple(group);
  }

  /* Stop the page jumping when you tap a size. The theme hides each size's radio
     with position:absolute (sr-only), so tapping a label focuses an off-screen
     input and the browser scrolls it into view; the variant re-render a moment
     later can nudge it again. We pin the scroll position for a short window after
     a swatch is tapped, and bail out the instant the shopper actually scrolls. */
  function guardVariantScroll() {
    if (window.__maareeVPScroll) return;
    window.__maareeVPScroll = true;
    var savedY = 0, until = 0, active = false, touchY = null;

    function pin() {
      if (!active) return;
      if (Date.now() > until) { active = false; return; }
      if (Math.abs(window.scrollY - savedY) > 1) window.scrollTo(0, savedY);
      requestAnimationFrame(pin);
    }
    function start() {
      savedY = window.scrollY;
      until = Date.now() + 800;            // hold through focus + variant re-render
      if (!active) { active = true; requestAnimationFrame(pin); }
    }
    function stop() { active = false; }

    document.addEventListener('pointerdown', function (e) {
      if (e.target && e.target.closest && e.target.closest('.variant-picker')) start();
    }, true);

    // let the shopper override the pin the moment they genuinely scroll
    document.addEventListener('wheel', stop, { passive: true, capture: true });
    document.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'ArrowUp' || k === 'ArrowDown' || k === 'PageUp' || k === 'PageDown' || k === 'Home' || k === 'End' || k === ' ' || k === 'Spacebar') stop();
    }, true);
    document.addEventListener('touchstart', function (e) {
      touchY = (e.touches && e.touches[0]) ? e.touches[0].clientY : null;
    }, { passive: true, capture: true });
    document.addEventListener('touchmove', function (e) {
      if (touchY != null && e.touches && e.touches[0] && Math.abs(e.touches[0].clientY - touchY) > 10) stop();
    }, { passive: true, capture: true });
  }

  /* ============================================================
     SIZE GATE — empty by default; Fit Finder fills it in
     ============================================================
     No size is pre-selected on load. If the Fit Finder has an in-stock
     recommendation for this PDP's style, we auto-select it. Otherwise the picker
     stays empty and Add to bag is disabled ("Select your size") until the shopper
     picks one. Selecting mirrors a real click so the theme's own variant/cart
     wiring stays intact. A per-page flag stops us re-clearing a real choice. */
  var __sizeChosen = false;
  var __gateCleared = false;   // the one-time "clear the theme default" pass has run

  function pickers() { return Array.prototype.slice.call(document.querySelectorAll('variant-picker')); }

  function sizeGroups(picker) {
    var groups = [];
    var combined = picker.querySelector('.variant-picker__combined-selector');
    if (combined) {
      var ubRow = combined.querySelector('fieldset .variant-picker__option-values:not(.hidden)');
      var cupRow = combined.querySelector('.variant--cup');
      if (ubRow) groups.push(ubRow.querySelectorAll('input[type="radio"]'));
      if (cupRow) groups.push(cupRow.querySelectorAll('input[type="radio"]'));
    } else {
      picker.querySelectorAll('fieldset.variant-picker__option').forEach(function (f) {
        var row = f.querySelector('.variant-picker__option-values:not(.hidden)');
        if (row) { var r = row.querySelectorAll('input[type="radio"]'); if (r.length) groups.push(r); }
      });
    }
    return groups;
  }
  function allChosen(picker) {
    var g = sizeGroups(picker);
    return g.length > 0 && g.every(function (radios) {
      return Array.prototype.some.call(radios, function (r) { return r.checked; });
    });
  }
  function clearSelection(picker) {
    picker.querySelectorAll('input[type="radio"]').forEach(function (r) { r.checked = false; });
    picker.querySelectorAll('.block-swatch.is-selected, .thumbnail-swatch.is-selected').forEach(function (l) { l.classList.remove('is-selected'); });
  }

  function gateBuyButtons(on) {
    document.querySelectorAll('product-form').forEach(function (pf) {
      pf.classList.toggle('maaree-needs-size', on);
      var btn = pf.querySelector('buy-buttons button[type="submit"]');
      if (!btn) return;
      if (on) {
        if (!btn.dataset.maareeGated) { btn.dataset.maareeOrig = btn.innerHTML; btn.dataset.maareeGated = '1'; }
        btn.setAttribute('disabled', '');
        btn.setAttribute('aria-disabled', 'true');
        btn.innerHTML = '<span>Select your size</span>';
      } else if (btn.dataset.maareeGated) {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
        if (btn.dataset.maareeOrig != null) btn.innerHTML = btn.dataset.maareeOrig;
        delete btn.dataset.maareeGated;
        delete btn.dataset.maareeOrig;
      }
    });
  }

  function swatchLabelByText(scope, txt) {
    if (!scope || txt == null) return null;
    var want = String(txt).toUpperCase().replace(/\s+/g, '');
    var found = null;
    scope.querySelectorAll('.block-swatch').forEach(function (l) {
      if (found) return;
      // Match on the raw UK value (data-uk), not the currently-visible text --
      // the Fit Finder's recommendation is always in raw UK terms, and the
      // visible text can be showing a converted AU number when that toggle is
      // active.
      var raw = l.getAttribute('data-uk');
      var t;
      if (raw) {
        t = raw.trim().toUpperCase().replace(/\s+/g, '');
      } else {
        var s = l.querySelector('span');
        t = ((s ? s.textContent : l.textContent) || '').trim().toUpperCase().replace(/\s+/g, '');
      }
      if (t === want) found = l;
    });
    return found;
  }
  function checkByLabel(label) {
    if (!label) return false;
    var input = label.htmlFor ? document.getElementById(label.htmlFor) : null;
    if (!input && label.previousElementSibling && label.previousElementSibling.tagName === 'INPUT') input = label.previousElementSibling;
    if (!input) return false;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function currentStyleKey() {
    var btn = document.querySelector('.maaree-find-size');
    return btn ? (btn.getAttribute('data-style') || 'solidarity') : null;
  }
  function storedRec() {
    if (!(window.MaareeFitFinder && typeof window.MaareeFitFinder.getStored === 'function')) return null;
    var s = window.MaareeFitFinder.getStored();
    if (!s || !s.recommendations) return null;
    var key = currentStyleKey();
    if (!key) return null;
    var rec = s.recommendations[key];
    if (!rec || rec.unavailable || !rec.size) return null;
    return rec;
  }

  function recInStock(picker, size) {
    var combined = picker.querySelector('.variant-picker__combined-selector');
    var scope = combined
      ? combined.querySelector('.variant-picker__option-values.hidden')
      : picker.querySelector('fieldset.variant-picker__option .variant-picker__option-values:not(.hidden)');
    var lab = swatchLabelByText(scope, size);
    return !!(lab && !lab.classList.contains('is-disabled'));
  }

  function selectFromRec(picker) {
    var rec = storedRec();
    if (!rec) return false;
    var size = String(rec.size).toUpperCase().replace(/\s+/g, '');
    if (!recInStock(picker, size)) return false;
    var combined = picker.querySelector('.variant-picker__combined-selector');
    if (combined) {
      var band = rec.band || (size.match(/^\d+/) || [])[0];
      var cup = rec.cup || (size.match(/[A-Z]+$/) || [])[0];
      var ubRow = combined.querySelector('fieldset .variant-picker__option-values:not(.hidden)');
      var cupRow = combined.querySelector('.variant--cup');
      var a = checkByLabel(swatchLabelByText(ubRow, band));
      var b = checkByLabel(swatchLabelByText(cupRow, cup));
      return a && b;
    }
    var row = picker.querySelector('fieldset.variant-picker__option .variant-picker__option-values:not(.hidden)');
    return checkByLabel(swatchLabelByText(row, size));
  }

  /* Gate the buy button purely from the live DOM: disabled until every picker has
     a full choice. Safe to call as often as we like; it never wipes a selection. */
  function gateNow() {
    // A size is always selected on this theme (we no longer clear it), so the buy
    // button is never disabled. Kept as a no-op enabler so a re-render always
    // restores the real "Add to bag" label if anything else touched it.
    gateBuyButtons(false);
  }

  /* Force the Fit Finder's recommended size into the picker (if it is in stock). */
  function applyFitRec() {
    __gateCleared = true;   // an explicit recommendation must not be re-cleared
    pickers().forEach(function (picker) {
      if (selectFromRec(picker)) { __sizeChosen = true; }
    });
    gateNow();
  }

  function enforceSizeGate() {
    // Intentionally a no-op. We used to auto-select the Fit Finder's recommended
    // size here, but on the Solidarity bra each colour is a SEPARATE product, so
    // changing colour reloads the page and this re-ran -- overriding the size the
    // shopper had chosen and snapping it back to the recommendation every time.
    // The recommendation is now display-only (the "Recommended size" badge); the
    // shopper selects the size themselves, and we never clear or override it.
  }

  function setupSizeGate() {
    if (window.__maareeSizeGate) return;
    window.__maareeSizeGate = true;
    // Keep the buy button on its real "Add to bag" label after any re-render.
    document.addEventListener('change', function (e) {
      var p = e.target && e.target.closest ? e.target.closest('variant-picker') : null;
      if (p) gateNow();
    }, true);
  }

  function bindSoldOut() {
    splitSelectors().forEach(function (root) {
      if (root.__maareeSplitBound) return;
      root.__maareeSplitBound = true;
      root.addEventListener('change', function () {
        // let the theme reconcile the checked state first
        setTimeout(applySoldOut, 0);
      });
    });
    applySoldOut();
  }

  /* Re-sync the UK/AU (or other local) size-unit conversion labels. Delegates to
     the function variant-picker.liquid exposes on window.MaareeSizeUnit. Safe to
     call as often as we like -- it always re-queries the live DOM, so it will
     correctly pick up swatches/toggles that Maestrooo's re-render just replaced. */
  function resyncSizeUnit() {
    if (window.MaareeSizeUnit && typeof window.MaareeSizeUnit.refresh === 'function') {
      window.MaareeSizeUnit.refresh();
    }
  }

  /* --------------------------- Multipack --------------------------- */
  function quantityInputFor(pack) {
    var formId = pack.getAttribute('data-form');
    if (formId) {
      var byForm = document.querySelector('input[name="quantity"][form="' + formId + '"]');
      if (byForm) return byForm;
    }
    return pack.querySelector('.maaree-pack__qty') ||
           document.querySelector('.product-info input[name="quantity"]');
  }

  function pickPack(pack, opt) {
    pack.querySelectorAll('.maaree-pack__opt').forEach(function (o) {
      o.classList.remove('is-active');
      o.setAttribute('aria-selected', 'false');
    });
    opt.classList.add('is-active');
    opt.setAttribute('aria-selected', 'true');

    var name = opt.getAttribute('data-name') || '';
    var sub = opt.getAttribute('data-sub') || '';
    var price = opt.getAttribute('data-price') || '';
    var save = opt.getAttribute('data-save') || '';
    var qty = parseInt(opt.getAttribute('data-qty'), 10) || 1;

    var nameEl = pack.querySelector('.maaree-pack__name');
    var subEl = pack.querySelector('.maaree-pack__sub');
    var priceEl = pack.querySelector('.maaree-pack__price');
    if (nameEl) nameEl.textContent = name;
    if (subEl) subEl.textContent = sub;
    if (priceEl) priceEl.innerHTML = price + (save ? '<small>' + save + '</small>' : '');

    var qtyInput = quantityInputFor(pack);
    if (qtyInput) {
      qtyInput.value = qty;
      qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    pack.classList.remove('is-open');
    var trigger = pack.querySelector('.maaree-pack__trigger');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  /* How many units are buyable for the currently selected variant.
     The snippet writes data-stock; empty/missing = untracked or oversell = unlimited. */
  function packStock(pack) {
    var raw = pack.getAttribute('data-stock');
    if (raw == null || raw === '') return Infinity;
    var n = parseInt(raw, 10);
    if (isNaN(n)) return Infinity;
    return n < 0 ? 0 : n;
  }

  function applyPackAvailability(pack) {
    var stock = packStock(pack);
    var opts = Array.prototype.slice.call(pack.querySelectorAll('.maaree-pack__opt'));
    var bestEnabled = null;

    opts.forEach(function (opt) {
      var qty = parseInt(opt.getAttribute('data-qty'), 10) || 1;
      // Only grey a pack when there's a known POSITIVE stock that's too low for it
      // (e.g. 2 left -> grey Pack of 3). Stock 0 = sold out (buy button handles it),
      // empty/unlimited = no greying. Never greys the single bra.
      var disabled = stock > 0 && qty > stock;
      opt.classList.toggle('is-unavailable', disabled);
      opt.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      if (!disabled) {
        if (!bestEnabled || qty > (parseInt(bestEnabled.getAttribute('data-qty'), 10) || 1)) bestEnabled = opt;
      }
    });

    // If the chosen pack no longer fits the stock, drop to the biggest one that does.
    var active = pack.querySelector('.maaree-pack__opt.is-active');
    if (active && active.classList.contains('is-unavailable') && bestEnabled) {
      pickPack(pack, bestEnabled);
    }
  }

  function refreshPacks() {
    document.querySelectorAll('.maaree-pack').forEach(applyPackAvailability);
  }

  function initPack(pack) {
    if (pack.__maareeBound) { applyPackAvailability(pack); return; }
    pack.__maareeBound = true;
    var trigger = pack.querySelector('.maaree-pack__trigger');
    if (trigger) {
      trigger.addEventListener('click', function () {
        var open = pack.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    pack.querySelectorAll('.maaree-pack__opt').forEach(function (opt) {
      opt.addEventListener('click', function () {
        if (opt.classList.contains('is-unavailable')) return; // not enough stock
        pickPack(pack, opt);
      });
    });
    applyPackAvailability(pack);
  }

  /* --------------------------- Add to your order (quick add) --------------------------- */
  function initAddOrder() {
    document.querySelectorAll('.maaree-ao__card').forEach(function (card) {
      var btn = card.querySelector('.maaree-ao__add');
      if (!btn) return;
      var sel = card.querySelector('.maaree-ao__size');
      var priceEl = card.querySelector('[data-ao-price]');

      // Size dropdown: keep the button's variant id in sync with the chosen size,
      // and reflect that size's price.
      if (sel && !sel.__maareeBound) {
        sel.__maareeBound = true;
        sel.addEventListener('change', function () {
          var opt = sel.options[sel.selectedIndex];
          btn.setAttribute('data-variant-id', sel.value || '');
          sel.classList.remove('is-error');
          if (priceEl && opt && opt.getAttribute('data-price')) {
            priceEl.textContent = opt.getAttribute('data-price');
          }
        });
      }

      if (btn.__maareeBound) return;
      btn.__maareeBound = true;
      btn.addEventListener('click', function () {
        if (btn.classList.contains('is-added')) return;
        var id = btn.getAttribute('data-variant-id');
        if (!id) {
          // Multi-size product with no size chosen yet: nudge the dropdown.
          if (sel) { sel.classList.add('is-error'); sel.focus(); }
          return;
        }
        btn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
        }).then(function (r) {
          btn.disabled = false;
          if (r.ok) {
            btn.classList.add('is-added');
            btn.innerHTML = '<svg class="maaree-ao__plus" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M5 12l5 5 9-11"/></svg><span class="maaree-ao__addtxt">Added</span>';
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
        }).catch(function () { btn.disabled = false; });
      });
    });
  }

  /* ============================================================
     BATCH 1 — overlays on the first gallery image + pairs FAB
     ============================================================ */
  function overlayHostEl() {
    // The stable, non-scrolling image viewport wrapper. The badges live here so
    // they stay pinned over the photo area and DON'T scroll away when you swipe
    // between images (mobile free-scroll) or sit on one slide. Falls back to the
    // gallery element, then a single media slide, if the theme markup differs.
    return document.querySelector('.product-gallery__image-list')
        || document.querySelector('product-gallery')
        || document.querySelector('.product-gallery__media');
  }

  function ensureOverlayHost() {
    var host = overlayHostEl();
    if (!host) return null;
    host.classList.add('maaree-has-overlay');
    return host;
  }

  // The model caption belongs to a specific photo, so it pins to the first
  // visible IMAGE slide (bottom-left), not the whole viewport. On desktop
  // grid this keeps it on the main image instead of the bottom of the grid.
  function firstImageMedia() {
    return document.querySelector('.product-gallery__media[data-media-type="image"]:not([hidden])')
        || document.querySelector('.product-gallery__media:not([hidden])')
        || document.querySelector('.product-gallery__media');
  }
  function ensureModelHost() {
    var m = firstImageMedia();
    if (!m) return null;
    m.classList.add('maaree-has-overlay');
    return m;
  }

  /* (1) Eyebrow badge -> top-left of the first image.
     On mobile we reduce it to a single badge (text before the first dot). */
  function buildEyebrowOverlay() {
    var host = ensureOverlayHost();
    if (!host) return;
    var src = document.querySelector('.product-info .maaree-eyebrow');
    var existing = host.querySelector('.maaree-img-eyebrow');
    var text = src ? (src.textContent || '').trim() : '';

    // Same eyebrow, same position, on every screen size.
    if (!src || !text) { if (existing) existing.remove(); return; }

    var el = existing;
    if (!el) {
      el = document.createElement('span');
      el.className = 'maaree-img-eyebrow';
      host.appendChild(el);
    }
    if (el.textContent !== text) el.textContent = text;

    // hide the in-flow original so the right column frees up
    var origItem = src.closest('.product-info__block-item');
    if (origItem) origItem.classList.add('maaree-has-overlay-dupe');
  }

  /* (model statics) -> bottom-left of the first image. */
  function buildModelOverlay() {
    // Desktop (grid): pin to the first image slide (bottom-left of that photo).
    // Mobile (free-scroll carousel): pin to the stable image viewport wrapper,
    // which is exactly one image tall on a phone, so the line reliably lands on
    // the photo instead of falling below it. The image viewport host also moves
    // any previously-created overlay if we cross the breakpoint.
    var host = isMobile() ? ensureOverlayHost() : ensureModelHost();
    if (!host) return;
    var src = document.querySelector('.maaree-model-size');
    // find an existing overlay anywhere (it may be in the other host if we just
    // crossed the mobile/desktop breakpoint)
    var existing = document.querySelector('.maaree-img-model');
    // Guard on TEXT, not HTML: an empty metafield still renders <p><em></em></p>,
    // which would otherwise paint an empty grey bar on the image. Also REMOVE a
    // stale overlay if we switch to a colour/product whose model field is empty.
    var txt = src ? (src.textContent || '').trim() : '';
    if (!src || !txt) {
      if (existing) existing.remove();
      // Empty model metafield: hide the below-gallery box too, otherwise it
      // paints as an empty grey bar under the images (seen on the
      // accessories template, 12 Jun 2026). It used to be un-hidden here.
      if (src) src.classList.add('maaree-has-overlay-dupe');
      return;
    }

    var el = existing;
    if (!el) {
      el = document.createElement('span');
      el.className = 'maaree-img-model';
    }
    if (el.parentElement !== host) host.appendChild(el); // create or MOVE to current host
    var modelHtml = src.innerHTML.trim();
    if (el.innerHTML !== modelHtml) el.innerHTML = modelHtml;

    // hide the original below-gallery box
    src.classList.add('maaree-has-overlay-dupe');
  }

  /* (pairs well with — mobile) pill top-right of the first image,
     jumps to the upsell / complete-the-look section lower down. */
  function pairsTarget() {
    var t = document.querySelector('#shopify-section-pdp_ctl')
         || document.querySelector('.maaree-ctl')
         || document.querySelector('.maaree-ao')
         || document.querySelector('#shopify-section-pdp_related');
    if (t && !t.id) t.id = 'maaree-pairs-target';
    return t;
  }

  function buildPairsFab() {
    var host = ensureOverlayHost();
    if (!host) return;
    if (host.querySelector('.maaree-pairs-fab')) return;
    var target = pairsTarget();
    if (!target) return;

    var a = document.createElement('a');
    a.className = 'maaree-pairs-fab';
    a.href = '#' + target.id;
    a.innerHTML = '<span class="maaree-pairs-fab__txt">Pairs with</span>' +
                  '<span class="maaree-pairs-fab__chev"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg></span>';
    a.addEventListener('click', function (e) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    host.appendChild(a);
  }

  /* (pairs well with — desktop) button bottom-right of the SECOND image,
     same jump target as the mobile pill. */
  function secondImageMedia() {
    var imgs = document.querySelectorAll('.product-gallery__media[data-media-type="image"]:not([hidden])');
    return imgs[1] || imgs[0] || null;
  }
  function buildPairsDesktop() {
    var media = secondImageMedia();
    if (!media) return;
    if (media.querySelector('.maaree-pairs-fab--desktop')) return;
    var target = pairsTarget();
    if (!target) return;
    media.classList.add('maaree-has-overlay');
    var a = document.createElement('a');
    a.className = 'maaree-pairs-fab maaree-pairs-fab--desktop';
    a.href = '#' + target.id;
    a.innerHTML = '<span class="maaree-pairs-fab__txt">Pairs with</span>' +
                  '<span class="maaree-pairs-fab__chev"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg></span>';
    a.addEventListener('click', function (e) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    media.appendChild(a);
  }

  function buildGalleryEnhancements() {
    buildEyebrowOverlay();
    buildModelOverlay();
    buildPairsFab();
    buildPairsDesktop();
    refreshFitLabels();
    bindSoldOut();
    refreshPacks();
    resyncSizeUnit();
  }

  /* --------------------------- Wire up --------------------------- */
  function init() {
    findSizeButtons().forEach(function (btn) {
      if (btn.__maareeBound) return;
      btn.__maareeBound = true;
      btn.addEventListener('click', function () {
        openFinder(btn.getAttribute('data-style') || 'solidarity');
      });
    });
    document.querySelectorAll('.maaree-pack').forEach(initPack);
    initAddOrder();
    bindSoldOut();
    guardVariantScroll();

    // Reviews: reveal the Stamped widget on this page (no jump to another page)
    document.querySelectorAll('.maaree-rev__toggle').forEach(function (btn) {
      if (btn.__maareeBound) return;
      btn.__maareeBound = true;
      btn.addEventListener('click', function () {
        var w = btn.parentNode.querySelector('.maaree-rev__widget');
        if (!w) return;
        var open = w.hasAttribute('hidden');
        if (open) { w.removeAttribute('hidden'); btn.setAttribute('aria-expanded', 'true'); }
        else { w.setAttribute('hidden', ''); btn.setAttribute('aria-expanded', 'false'); }
        if (window.StampedFn && window.StampedFn.loadBadges) { try { window.StampedFn.loadBadges(); } catch (e) {} }
      });
    });

    // close any open pack dropdown on outside click
    if (!window.__maareePackOutside) {
      window.__maareePackOutside = true;
      document.addEventListener('click', function (e) {
        document.querySelectorAll('.maaree-pack.is-open').forEach(function (pack) {
          if (!pack.contains(e.target)) {
            pack.classList.remove('is-open');
            var t = pack.querySelector('.maaree-pack__trigger');
            if (t) t.setAttribute('aria-expanded', 'false');
          }
        });
      });
    }

    buildGalleryEnhancements();
    restoreStored();
    setupSizeGate();
    enforceSizeGate();
    window.addEventListener('maaree:fit-result', function (e) {
      applyResult(e.detail);      // update the "Recommended size" badge ONLY -- we no
                                  // longer auto-select it (it fought colour changes).
    });
  }

  /* Re-apply enhancements after the buy box / gallery re-renders on a variant
     change (Maestrooo's <product-rerender> can replace nodes). Debounced and
     idempotent so it is safe to fire often. */
  var reTimer = null;
  var reRAF = null;
  var mo = null;            // the product-subtree MutationObserver (set in observe())
  function reobserve() {
    var root = document.querySelector('product-rerender') || document.querySelector('.product');
    if (mo && root) mo.observe(root, { childList: true, subtree: true });
  }
  function runRebuild() {
    // Disconnect the observer while we rewrite the overlays/labels. Otherwise
    // our own DOM writes (eyebrow text, model innerHTML, fit-pill labels) are
    // caught by the observer below and reschedule us again -- a permanent
    // background loop (~3-4 rebuilds/sec, forever) that made every PDP feel
    // slow and added lag when changing size. Reconnect once we are done.
    if (mo) mo.disconnect();
    try {
      document.querySelectorAll('.maaree-pack').forEach(initPack);
      initAddOrder();
      buildGalleryEnhancements();
      enforceSizeGate();
    } finally {
      reobserve();
    }
  }
  /* The flat 120ms-only debounce below used to be the sole trigger for
     re-applying the crossed-out/greyed size states after a variant change --
     Maestrooo swaps in fresh swatches (always the theme's raw default state,
     no cross-colour greying) the instant a size is picked, and for up to
     120ms nothing had corrected them, producing a visible flash on every
     size-to-size change (not just when the UK/AU unit toggle was involved).
     Now we ALSO fire on the next animation frame -- before the browser paints
     the raw swap in the common case -- while keeping the original 120ms
     timer as a safety net for mutations that land a beat later. runRebuild()
     is idempotent (guarded by the observer disconnect/reconnect above), so
     running it twice in quick succession is harmless. */
  function scheduleRebuild() {
    if (!reRAF) {
      reRAF = requestAnimationFrame(function () {
        reRAF = null;
        runRebuild();
      });
    }
    clearTimeout(reTimer);
    reTimer = setTimeout(runRebuild, 120);
  }

  function observe() {
    var root = document.querySelector('product-rerender') || document.querySelector('.product');
    if (root && window.MutationObserver) {
      mo = new MutationObserver(scheduleRebuild);
      mo.observe(root, { childList: true, subtree: true });
    }
    // crossing the mobile breakpoint: relabel fit pills AND re-home the model
    // overlay (slide on desktop, viewport on mobile)
    function onBpChange() { refreshFitLabels(); scheduleRebuild(); }
    try { window.matchMedia(MOBILE_Q).addEventListener('change', onBpChange); }
    catch (e) { window.addEventListener('resize', onBpChange); }
    // theme also fires this on the form
    document.addEventListener('variant:change', scheduleRebuild);
  }

  function boot() {
    init();
    observe();
    // Mobile galleries (free-scroll carousel) can finish initialising after our
    // first pass; re-apply on full load and a couple of short delays so the
    // model line reliably lands on the first image instead of falling below it.
    window.addEventListener('load', scheduleRebuild);
    setTimeout(scheduleRebuild, 400);
    setTimeout(scheduleRebuild, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();